<template>
  <!-- Only show in development/test mode -->
  <div v-if="isTestMode" class="test-helper">
    <!-- Floating Toggle Button -->
    <button 
      class="test-helper-toggle"
      @click="isOpen = !isOpen"
      :class="{ 'is-open': isOpen }"
      title="Test Data Helper (Press T to toggle)"
    >
      <svg v-if="!isOpen" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2v-4M9 21H5a2 2 0 01-2-2v-4"/>
        <circle cx="12" cy="12" r="1"/>
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>

    <!-- Quick Copy Buttons (always visible when panel closed) -->
    <Transition name="fade">
      <div v-if="!isOpen && showQuickButtons" class="quick-buttons">
        <button @click="quickFill(visaCards[0])" class="quick-btn" title="Visa (Skip 3DS)">
          <span class="quick-label">V1</span>
        </button>
        <button @click="quickFill(mastercardCards[0])" class="quick-btn" title="Mastercard (Skip 3DS)">
          <span class="quick-label">M1</span>
        </button>
      </div>
    </Transition>

    <!-- Test Data Panel -->
    <Transition name="slide">
      <div v-if="isOpen" class="test-panel">
        <div class="test-panel-header">
          <h3>🧪 Dintero Test Data</h3>
          <span class="test-badge">TEST MODE</span>
        </div>

        <div class="test-panel-content">
          <!-- Selected Card Display -->
          <div v-if="selectedCard" class="selected-card">
            <div class="selected-card-header">
              <span class="selected-label">Selected Card</span>
              <button @click="selectedCard = null" class="clear-btn">Clear</button>
            </div>
            <div class="selected-card-fields">
              <button @click="copyField('number')" class="field-btn" :class="{ copied: copiedField === 'number' }">
                <span class="field-label">Card Number</span>
                <span class="field-value">{{ formatCardNumber(selectedCard.number) }}</span>
                <span class="field-action">{{ copiedField === 'number' ? '✓ Copied!' : 'Click to copy' }}</span>
              </button>
              <div class="field-row">
                <button @click="copyField('expiry')" class="field-btn small" :class="{ copied: copiedField === 'expiry' }">
                  <span class="field-label">Expiry</span>
                  <span class="field-value">{{ selectedCard.expiry }}</span>
                  <span class="field-action">{{ copiedField === 'expiry' ? '✓' : 'Copy' }}</span>
                </button>
                <button @click="copyField('cvv')" class="field-btn small" :class="{ copied: copiedField === 'cvv' }">
                  <span class="field-label">CVV</span>
                  <span class="field-value">{{ selectedCard.cvv }}</span>
                  <span class="field-action">{{ copiedField === 'cvv' ? '✓' : 'Copy' }}</span>
                </button>
              </div>
            </div>
            <p class="paste-hint">💡 Click each field above, then paste (Ctrl+V) into the payment form</p>
          </div>

          <!-- Card Section -->
          <div class="test-section">
            <h4>💳 Credit Cards (3D-Secure)</h4>
            
            <div class="test-subsection">
              <h5>Visa <kbd>1-4</kbd></h5>
              <div class="test-cards">
                <button 
                  v-for="(card, index) in visaCards" 
                  :key="card.number"
                  @click="selectCard(card)"
                  class="test-card-btn"
                  :class="[card.type, { selected: selectedCard?.number === card.number }]"
                >
                  <span class="card-shortcut">{{ index + 1 }}</span>
                  <span class="card-number">{{ formatCardNumber(card.number) }}</span>
                  <span class="card-desc">{{ card.description }}</span>
                </button>
              </div>
            </div>

            <div class="test-subsection">
              <h5>Mastercard <kbd>5-8</kbd></h5>
              <div class="test-cards">
                <button 
                  v-for="(card, index) in mastercardCards" 
                  :key="card.number"
                  @click="selectCard(card)"
                  class="test-card-btn"
                  :class="[card.type, { selected: selectedCard?.number === card.number }]"
                >
                  <span class="card-shortcut">{{ index + 5 }}</span>
                  <span class="card-number">{{ formatCardNumber(card.number) }}</span>
                  <span class="card-desc">{{ card.description }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Declined Cards Section -->
          <div class="test-section">
            <h4>❌ Declined Cards <kbd>9-0</kbd></h4>
            <div class="test-cards">
              <button 
                v-for="(card, index) in declinedCards" 
                :key="card.number"
                @click="selectCard(card)"
                class="test-card-btn declined"
                :class="{ selected: selectedCard?.number === card.number }"
              >
                <span class="card-shortcut">{{ index === 0 ? '9' : '0' }}</span>
                <span class="card-number">{{ formatCardNumber(card.number) }}</span>
                <span class="card-desc">{{ card.description }}</span>
              </button>
            </div>
          </div>

          <!-- Vipps Section -->
          <div class="test-section">
            <h4>📱 Vipps</h4>
            <button @click="copyToClipboard('+4740230256', 'vipps')" class="test-copy-btn" :class="{ copied: copiedField === 'vipps' }">
              <span>+47 40230256</span>
              <span class="copy-hint">{{ copiedField === 'vipps' ? '✓ Copied!' : 'Click to copy' }}</span>
            </button>
            <p class="test-note">Requires Vipps test app</p>
          </div>

          <!-- Keyboard Shortcuts -->
          <div class="shortcuts-info">
            <h4>⌨️ Keyboard Shortcuts</h4>
            <div class="shortcut-list">
              <span><kbd>T</kbd> Toggle panel</span>
              <span><kbd>1-8</kbd> Select card</span>
              <span><kbd>C</kbd> Copy card number</span>
              <span><kbd>E</kbd> Copy expiry</span>
              <span><kbd>V</kbd> Copy CVV</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const isOpen = ref(false)
const showQuickButtons = ref(true)
const selectedCard = ref(null)
const copiedField = ref(null)

// Only show in test environment (account starts with T)
const isTestMode = true // Could check env variable or account ID

const visaCards = [
  { number: '4000000000000002', description: 'Skip 3DS challenge ✓', type: 'success', expiry: '12/28', cvv: '123' },
  { number: '4000100000000000', description: '3DS with challenge', type: 'success', expiry: '12/28', cvv: '123' },
  { number: '4000200000000008', description: '3DS proof of attempt', type: 'success', expiry: '12/28', cvv: '123' },
  { number: '4000300000000006', description: '3DS verification fails', type: 'warning', expiry: '12/28', cvv: '123' },
]

const mastercardCards = [
  { number: '5200000000000007', description: 'Skip 3DS challenge ✓', type: 'success', expiry: '12/28', cvv: '123' },
  { number: '5200000000000015', description: '3DS with challenge', type: 'success', expiry: '12/28', cvv: '123' },
  { number: '5200000000000023', description: '3DS proof of attempt', type: 'success', expiry: '12/28', cvv: '123' },
  { number: '5200000000000031', description: '3DS verification fails', type: 'warning', expiry: '12/28', cvv: '123' },
]

const declinedCards = [
  { number: '4100000000000001', description: 'Authorization timeout', type: 'error', expiry: '12/28', cvv: '123' },
  { number: '5100000000000008', description: 'Authorization timeout', type: 'error', expiry: '12/28', cvv: '123' },
]

const allCards = [...visaCards, ...mastercardCards, ...declinedCards]

function formatCardNumber(number) {
  return number.replace(/(.{4})/g, '$1 ').trim()
}

function selectCard(card) {
  selectedCard.value = card
  copyField('number')
}

function quickFill(card) {
  selectedCard.value = card
  isOpen.value = true
  copyField('number')
}

async function copyField(field) {
  if (!selectedCard.value) return
  
  let value = ''
  switch (field) {
    case 'number':
      value = selectedCard.value.number
      break
    case 'expiry':
      value = selectedCard.value.expiry
      break
    case 'cvv':
      value = selectedCard.value.cvv
      break
  }
  
  await copyToClipboard(value, field)
}

async function copyToClipboard(text, field) {
  try {
    await navigator.clipboard.writeText(text.replace(/\s/g, ''))
    copiedField.value = field
    showNotification(getNotificationMessage(field, text))
    
    // Reset copied state after 2 seconds
    setTimeout(() => {
      if (copiedField.value === field) {
        copiedField.value = null
      }
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

function getNotificationMessage(field, text) {
  switch (field) {
    case 'number':
      return `Card number copied! Paste with Ctrl+V`
    case 'expiry':
      return `Expiry "${text}" copied!`
    case 'cvv':
      return `CVV "${text}" copied!`
    case 'vipps':
      return `Phone number copied!`
    default:
      return `Copied: ${text}`
  }
}

function showNotification(message) {
  // Remove existing toasts
  document.querySelectorAll('.test-helper-toast').forEach(el => el.remove())
  
  // Create a toast notification
  const toast = document.createElement('div')
  toast.className = 'test-helper-toast'
  toast.innerHTML = `<span class="toast-icon">📋</span> ${message}`
  document.body.appendChild(toast)
  
  setTimeout(() => {
    toast.classList.add('show')
  }, 10)
  
  setTimeout(() => {
    toast.classList.remove('show')
    setTimeout(() => toast.remove(), 300)
  }, 2500)
}

// Keyboard shortcuts
function handleKeydown(e) {
  // Don't trigger if user is typing in an input
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
  
  const key = e.key.toLowerCase()
  
  // Toggle panel with T
  if (key === 't' && !e.ctrlKey && !e.metaKey) {
    e.preventDefault()
    isOpen.value = !isOpen.value
    return
  }
  
  // Number keys 1-8 to select cards, 9-0 for declined
  if (/^[1-9]$/.test(e.key)) {
    e.preventDefault()
    const index = parseInt(e.key) - 1
    if (index < allCards.length) {
      selectCard(allCards[index])
      if (!isOpen.value) isOpen.value = true
    }
    return
  }
  
  if (e.key === '0') {
    e.preventDefault()
    selectCard(declinedCards[1])
    if (!isOpen.value) isOpen.value = true
    return
  }
  
  // C to copy card number
  if (key === 'c' && selectedCard.value && !e.ctrlKey && !e.metaKey) {
    e.preventDefault()
    copyField('number')
    return
  }
  
  // E to copy expiry
  if (key === 'e' && selectedCard.value && !e.ctrlKey && !e.metaKey) {
    e.preventDefault()
    copyField('expiry')
    return
  }
  
  // V to copy CVV (not Ctrl+V which is paste)
  if (key === 'v' && selectedCard.value && !e.ctrlKey && !e.metaKey) {
    e.preventDefault()
    copyField('cvv')
    return
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.test-helper {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 10000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.test-helper-toggle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  transition: all 0.2s ease;
}

.test-helper-toggle:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(245, 158, 11, 0.5);
}

.test-helper-toggle.is-open {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.4);
}

.test-helper-toggle svg {
  width: 24px;
  height: 24px;
  stroke: white;
}

/* Quick buttons */
.quick-buttons {
  position: absolute;
  bottom: 0;
  right: 66px;
  display: flex;
  gap: 8px;
}

.quick-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 2px solid #f59e0b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.quick-btn:hover {
  transform: scale(1.1);
  background: #fef3c7;
}

.quick-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #d97706;
}

.test-panel {
  position: absolute;
  bottom: 70px;
  right: 0;
  width: 360px;
  max-height: 75vh;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.test-panel-header {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.test-panel-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.test-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.test-panel-content {
  padding: 16px;
  overflow-y: auto;
  flex: 1;
}

/* Selected Card Display */
.selected-card {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 2px solid #10b981;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 16px;
}

.selected-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.selected-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #059669;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.clear-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
}

.clear-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

.selected-card-fields {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 12px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  width: 100%;
}

.field-btn:hover {
  border-color: #10b981;
  background: #f0fdf4;
}

.field-btn.copied {
  border-color: #10b981;
  background: #d1fae5;
}

.field-btn.small {
  padding: 8px 10px;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.field-label {
  font-size: 0.65rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-value {
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.9rem;
  color: #1f2937;
  font-weight: 600;
}

.field-action {
  font-size: 0.65rem;
  color: #10b981;
  margin-top: 2px;
}

.paste-hint {
  font-size: 0.7rem;
  color: #059669;
  margin: 8px 0 0;
  text-align: center;
}

.test-section {
  margin-bottom: 16px;
}

.test-section h4 {
  margin: 0 0 8px;
  font-size: 0.9rem;
  color: #374151;
}

.test-subsection {
  margin-bottom: 12px;
}

.test-subsection h5 {
  margin: 0 0 6px;
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
}

kbd {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.65rem;
  font-family: 'SF Mono', 'Consolas', monospace;
  color: #6b7280;
}

.test-cards {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.test-card-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.test-card-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.test-card-btn.selected {
  border-color: #10b981;
  background: #ecfdf5;
}

.test-card-btn.success {
  border-left: 3px solid #10b981;
}

.test-card-btn.warning {
  border-left: 3px solid #f59e0b;
}

.test-card-btn.declined,
.test-card-btn.error {
  border-left: 3px solid #ef4444;
}

.card-shortcut {
  background: #e5e7eb;
  color: #4b5563;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
}

.test-card-btn.selected .card-shortcut {
  background: #10b981;
  color: white;
}

.card-number {
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.8rem;
  color: #1f2937;
  font-weight: 500;
  flex: 1;
}

.card-desc {
  font-size: 0.65rem;
  color: #6b7280;
  text-align: right;
}

.test-copy-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  cursor: pointer;
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.85rem;
}

.test-copy-btn:hover {
  background: #f3f4f6;
}

.test-copy-btn.copied {
  border-color: #10b981;
  background: #ecfdf5;
}

.copy-hint {
  font-size: 0.7rem;
  color: #9ca3af;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.test-copy-btn.copied .copy-hint {
  color: #10b981;
}

.test-note {
  font-size: 0.7rem;
  color: #9ca3af;
  margin: 6px 0 0;
}

/* Shortcuts info */
.shortcuts-info {
  background: #f3f4f6;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
}

.shortcuts-info h4 {
  margin: 0 0 8px;
  font-size: 0.8rem;
  color: #374151;
}

.shortcut-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.shortcut-list span {
  font-size: 0.7rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Animations */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Toast notification styles (global) */
:global(.test-helper-toast) {
  position: fixed;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  background: #1f2937;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 0.875rem;
  opacity: 0;
  transition: all 0.3s ease;
  z-index: 10001;
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

:global(.test-helper-toast .toast-icon) {
  font-size: 1rem;
}

:global(.test-helper-toast.show) {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* Mobile responsiveness */
@media (max-width: 400px) {
  .test-panel {
    width: calc(100vw - 40px);
    right: -10px;
    max-height: 60vh;
  }
  
  .card-number {
    font-size: 0.7rem;
  }
  
  .quick-buttons {
    display: none;
  }
}
</style>
